import { createContext, useContext, useEffect, useState } from 'react';

import { TeacherDeleteAssignmentMutationVariables } from '@school_ify/schoolify-repository/src/teacher/types';

import AssignmentsService from 'domain/services/dashboard/assignments/Index';
import usePagination from 'hooks/usePagination';

const AssignmentContext = createContext(null);

interface ToggleItem {
  id: string;
  due_at_filter: Date;
  is_completed: boolean;
  completed_at: string;
  show_all: boolean;
}

export default function UpcomingProvider({ children }) {
  const { hasNext, hasPrev, currentPage, setCurrentPage, setTotalCount, calculateOffset } =
    usePagination();
  const [toggleItem, setToggleItem] = useState<ToggleItem>();
  const [overdue, setOverdue] = useState<any[]>([]);
  const [today, setToday] = useState<any[]>([]);
  const [tomorrow, setTommorow] = useState<any[]>([]);

  // action="same" => we are pushing items to array while keeping the existing ones
  // actione="change" => we are removing all existing items in array and adding new
  async function getAssignments(action = 'same', show_completed = { _eq: false }, offset = 0) {
    const data = {
      show_completed,
      limit: 9,
      offset: action === 'change' ? 0 : offset,
    };

    const assignments = await AssignmentsService.getAssignments(data);
    setTotalCount(assignments.totalCount.aggregate.count);
    const { overdue, today, tomorrow } = AssignmentsService.groupByDate(assignments);

    if (action === 'change') {
      setCurrentPage(1);
      setOverdue(() => [...overdue]);
      setToday(() => [...today]);
      setTommorow(() => [...tomorrow]);
    } else {
      setOverdue(prevState => [...prevState, ...overdue]);
      setToday(prevState => [...prevState, ...today]);
      setTommorow(prevState => [...prevState, ...tomorrow]);
    }
  }

  function queryMore(showCompleted = { _eq: false }) {
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    getAssignments('same', showCompleted, calculateOffset(newPage));
  }

  function queryLess(showCompleted = { _eq: false }) {
    setCurrentPage(1);
    getAssignments('change', showCompleted, 0);
  }

  function clearToggledItem(item) {
    if (item.show_all) {
      return;
    }
    switch (item.due_at_filter) {
      case 'OVERDUE':
        setOverdue(overdue.filter(e => e.id !== item.id));
        break;
      case 'TODAY':
        setToday(today.filter(e => e.id !== item.id));
        break;
      default:
        setTommorow(tomorrow.filter(e => e.id !== item.id));
        break;
    }
  }

  async function toggleTheItem(item: ToggleItem) {
    try {
      if (!item) return;
      const submitedDate = item.is_completed ? item.completed_at : null;

      const data = {
        id: item.id,
        completed_at: submitedDate,
      };

      await AssignmentsService.toggleTeacherAssignment(data);
      clearToggledItem(item);
    } catch (err) {
      throw new Error(err);
    }
  }

  useEffect(() => {
    toggleTheItem(toggleItem);
  }, [toggleItem]);

  async function addAssignment(variable: any) {
    try {
      await AssignmentsService.addAssignment(variable);
    } catch (err) {
      throw new Error(`'Add assignment error: ${err}`);
    }
  }

  async function updateAssignment(payload: any) {
    const data = {
      _set: { ...payload },
      id: payload.id,
    };
    // eslint-disable-next-line no-underscore-dangle
    delete data._set.id;
    try {
      await AssignmentsService.updateAssignment(data);
    } catch (error) {
      throw new Error(error);
    }
  }

  async function deleteAssignment(payload: TeacherDeleteAssignmentMutationVariables) {
    try {
      await AssignmentsService.deleteAssignment(payload);
    } catch (error) {
      throw new Error(`'Delete assignment error: ${error}`);
    }
  }

  return (
    <AssignmentContext.Provider
      value={{
        hasPrev,
        hasNext,
        queryLess,
        queryMore,
        getAssignments,
        updateAssignment,
        deleteAssignment,
        data: {
          overdue,
          tomorrow,
          today,
        },
        setToggleItem,
        addAssignment,
      }}
    >
      {children}
    </AssignmentContext.Provider>
  );
}

export const useUpcoming = () => useContext(AssignmentContext);
